import { Injectable } from 'angular-ts-decorators';

import { PeriodType } from '@core/app/scripts/services/period-type';
import { unitOfTime } from 'moment';
import { Moment } from '@shared/app/scripts/vendor/moment';
import { DateSource } from '@shared/app/scripts/services/date-source';
import { includes, range } from 'lodash';

export interface IDateBetweenRange {
  gte: string;
  lt: string;
}

/**
 * Service that helps dealing with dates.
 *
 * Note! I'm assuming the UTC time zone what is not 100% correct - for now it's ok, but later will
 * need to updated.
 */
@Injectable('dateHelper')
export class DateHelper {
  constructor(
    private dateSource: DateSource,
    private periodType: PeriodType,
    private moment: Moment,
  ) {
    'ngInject';
  }

  /**
   *
   * Get query for the any date range.
   *
   * @param {date} startDate
   * selected start date
   * @param {date} endDate
   * selected end date
   * @return {object}
   * <pre>
   * {
   *   gte, # date start, in ISO 8601 format
   *   lt # date end, in ISO 8601 format
   * }
   * </pre>
   */
  getDateRangeQuery(startDate: Date, endDate: Date): IDateBetweenRange {
    if (!angular.isDate(startDate)) {
      throw new Error(
        'Needs date as a startDate parameter, <' +
          typeof startDate +
          '> provided instead',
      );
    }
    if (!angular.isDate(endDate)) {
      throw new Error(
        'Needs date as a endDate parameter, <' +
          typeof endDate +
          '> provided instead',
      );
    }
    const momentStartDate = this.moment(startDate)
        .utcOffset(0)
        .startOf(<unitOfTime.StartOf>this.periodType.day),
      momentEndDate = this.moment(endDate)
        .utcOffset(0)
        .endOf(<unitOfTime.StartOf>this.periodType.day);

    return <IDateBetweenRange>{
      gte: momentStartDate.format(),
      lt: momentEndDate.format(),
    };
  }

  /**
   *
   * Format a fragment for Mongo DB queries: Filter that makes mongo only
   * return data from the given time period.
   *
   * @param {string} periodTypeValue
   * Period type. One of the values of {@link dashboardApp.periodType
   * periodType} (periodType.day, periodType.week, etc.)
   * @param {date} date
   * period date - any date is valid, query will be formed from beginning to
   * the end of the period.
   * @return {object}
   * <pre>
   * {
   *   gte, # date start, in ISO 8601 format
   *   lt # date end, in ISO 8601 format
   * }
   * </pre>
   */
  getPeriodQuery(periodTypeValue: string, date: Date): IDateBetweenRange {
    if (!angular.isDate(date)) {
      throw new Error(
        'Needs date as a date parameter, <' +
          typeof date +
          '> provided instead',
      );
    }

    //TODO make period type into a enum for better existance check
    if (!includes(Object.keys(this.periodType), periodTypeValue)) {
      throw new Error(
        'periodTypeValue needs to be one of periodType values, <' +
          periodTypeValue +
          '> provided instead',
      );
    }

    // TODO SWDASH-28 solve globaly on core level
    // moment.js in default configuration consider sunday as begining of the week
    if (periodTypeValue === this.periodType.week) {
      periodTypeValue = 'isoweek';
    }

    const momentDate = this.moment(date);
    return <IDateBetweenRange>{
      gte: momentDate
        .clone()
        .startOf(<unitOfTime.StartOf>periodTypeValue)
        .format(),
      lt: momentDate
        .clone()
        .endOf(<unitOfTime.StartOf>periodTypeValue)
        .format(),
    };
  }

  /**
   *
   * Get equivalent date from last year
   *
   * @param {string} periodTypeValue
   * Period type. One of the values of {@link dashboardApp.periodType
   * periodType} (periodType.day, periodType.week, etc.)
   * @param {date} date
   * period date - any date is valid, query will be formed from beginning to
   * the end of the period.
   * @return {date}
   * equivalent date from last year
   */
  getCompareDate(periodTypeValue: string, date: Date): Date {
    let compareDate: Date;
    if (
      periodTypeValue === this.periodType.week ||
      periodTypeValue === this.periodType.day
    ) {
      return undefined;
    } else {
      compareDate = this.moment(date).subtract(1, 'year').toDate();
    }
    return compareDate;
  }

  /**
   *
   * Get number of days in the month
   *
   * @param {date} date
   * date from which days will be calculated
   * @return {number}
   * number of days in a month
   */
  getDaysInMonth(date: Date): number {
    if (!angular.isDate(date)) {
      throw new Error(
        'Needs date as a startDate parameter, <' +
          typeof date +
          '> provided instead',
      );
    }

    return this.moment(date).endOf(this.periodType.month).date();
  }

  /**
   *
   * Get hour number as hour label
   *
   * @param {number} hour
   * hour number
   * @return {string}
   * hour label
   */
  getHourLabel(hour: number): string {
    if (!angular.isNumber(hour)) {
      throw new Error(
        'Needs number as an hour parameter, <' +
          typeof hour +
          '> provided instead',
      );
    }

    if (hour < 0 || hour > 24) {
      throw new Error(
        'hour should be between 0 and 24, <' + hour + '> provided instead.',
      );
    }

    // TODO make local aware
    return this.moment(this.dateSource.now())
      .set('hour', hour)
      .startOf('hour')
      .format('HH:mm');
  }

  /**
   *
   * Get quarter number for a data
   *
   * @param {date} date
   * Date for getting quarter
   * @return {number}
   * quarter number
   */
  getQuarterNumber(date: Date): number {
    if (!angular.isDate(date)) {
      throw new Error(
        'Needs date as a startDate parameter, <' +
          typeof date +
          '> provided instead',
      );
    }

    return this.moment(date).quarter();
  }

  /**
   *
   * Get week number for a data
   *
   * @param {date} date
   * Date for getting week
   * @return {number}
   * week number
   */
  getWeekNumber(date: Date): number {
    if (!angular.isDate(date)) {
      throw new Error(
        'Needs date as a startDate parameter, <' +
          typeof date +
          '> provided instead',
      );
    }

    return this.moment(date).isoWeek();
  }

  /**
   * Method to generate labels for the different periodType scales.
   * @param {periodType} periodTypeValue
   * indicates what periodType we are preparing scale for
   * @param {date} periodDate
   * date indicating what period should be cover by the scale
   * @return {array<integer>}
   * Array with scale sequence
   */
  getScale(periodTypeValue: string, periodDate: Date): number[] {
    if (!angular.isDate(periodDate)) {
      throw new Error(
        'Needs date object as a periodDate parameter, <' +
          typeof periodDate +
          '> provided instead',
      );
    }
    if (!angular.isString(periodTypeValue)) {
      throw new Error(
        'Needs String as periodTypeValue parameter, <' +
          typeof periodTypeValue +
          '> provided instead',
      );
    }
    const periodMoment = this.moment(periodDate);

    switch (periodTypeValue) {
      case this.periodType.day:
        return range(24);
      case this.periodType.week:
        return range(1, 8);
      case this.periodType.month:
        return range(1, periodMoment.daysInMonth() + 1);
      case this.periodType.quarter:
        const month = periodMoment.month();
        return range(month + 1, month + 4);
      case this.periodType.year:
        return range(1, 13);
      default:
        throw new Error('Unknown period type: ' + periodTypeValue);
    }
  }

  // TODO write doc & tests
  getTimePoints(startDate: Date, endDate: Date, granularity: number): Date[] {
    const startMoment = this.moment(startDate).startOf('minute');
    const endMoment = this.moment(endDate).startOf('minute');

    const // minutes -> milliseconds
      timespan: number = granularity * 60 * 1000;

    const length: number =
      Math.ceil((endMoment.unix() - startMoment.unix()) / timespan) + 1;
    let pointIndex: number;
    const points: Date[] = [];

    points.length = length;

    for (pointIndex = 0; pointIndex < length; pointIndex++) {
      points[pointIndex] = startMoment
        .clone()
        .add(pointIndex * granularity, 'minutes')
        .toDate();
    }

    return points;
  }

  /**
   * Method to generate label for a one of the scale units
   * @param {periodType} periodTypeValue
   * indicates for what periodType we are doing the scale
   * @param {integer} number
   * number of the element to be displayed. 1 will mean Monday or January
   * depending of periodTypeValue.
   * @return {string}
   * String that represents the scale element in meaningful way
   */
  // TODO change method and arguments name to better reflect the meaning.
  getScaleLabel(periodTypeValue: string, number: number): string {
    if (!angular.isNumber(number)) {
      throw new Error(
        'Needs Integer as number parameter, <' +
          typeof number +
          '> provided instead',
      );
    }
    if (!angular.isString(periodTypeValue)) {
      throw new Error(
        'Needs String as periodTypeValue parameter, <' +
          typeof periodTypeValue +
          '> provided instead',
      );
    }

    switch (periodTypeValue) {
      case this.periodType.day:
        return this.getHourLabel(number);
      case this.periodType.week:
        return this.moment().isoWeekday(number).format('ddd');
      case this.periodType.month:
        return number.toString();
      case this.periodType.quarter:
        return this.moment()
          .month(number - 1)
          .format('MMM');
      case this.periodType.year:
        return this.moment()
          .month(number - 1)
          .format('MMM');
      default:
        throw new Error('Unknown period type: ' + periodTypeValue);
    }
  }

  /**
   * Method to generate label for data series for some date.
   * For (periodType.year, '2015-10-10') it will return '2015' as it's identifies the
   * year serie of data.
   * @param {periodType} periodTypeValue
   * indicates what information should be included in the retured string
   * @param {date} periodDate
   * date to display. It can be any where inside the period to get the same
   * result
   * @return {array}
   * String representing date in a context of periodTypeValue
   */
  getLabel(periodTypeValue: string, periodDate: Date): string {
    if (!angular.isDate(periodDate)) {
      throw new Error(
        'Needs date object as a periodDate parameter, <' +
          typeof periodDate +
          '> provided instead',
      );
    }
    if (!angular.isString(periodTypeValue)) {
      throw new Error(
        'Needs String as periodTypeValue parameter, <' +
          typeof periodTypeValue +
          '> provided instead',
      );
    }
    const periodMoment = this.moment(periodDate);

    // TODO CORE-51 throw expections when wrong arugments type
    // TODO CORE-51 cover with unit tests
    switch (periodTypeValue) {
      case this.periodType.day:
        return periodMoment.format('YYYY-MM-DD');
      case this.periodType.week:
        return periodMoment.format('W/YYYY');
      case this.periodType.month:
        return periodMoment.format('MMM YYYY');
      case this.periodType.quarter:
        return periodMoment.format('Q YYYY');
      case this.periodType.year:
        return periodMoment.format('YYYY');
      default:
        throw new Error('Unknown period type: ' + periodTypeValue);
    }
  }

  // TODO Cover with tests & refactor
  // TODO Change api and rename to getLastHourQuery
  getDateRange(date: Date, hours: number): Date[] {
    // TODO rewrite to really use moment
    const from: Date = this.moment(date).toDate();
    const timestamp: number = from.setHours(date.getHours() - hours);

    return [this.moment(timestamp).toDate(), date];
  }
}
